import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  ICampaignRequest,
  ICampaignTriggerKeyword,
  ICampaignTriggerMessage,
} from '@dataTypes/marketing.type'
import { Button } from '@components/common'
import { compareCampaignTriggerKeywordIds } from '@utils/marketing.utils'
import {
  AccordionComponent,
  AccordionItemDirective,
  AccordionItemsDirective,
} from '@syncfusion/ej2-react-navigations'
import TimePointsTable from './TriggerTemplates/TimePoints.component'
import ActionTrigger from './TriggerTemplates/ActionTrigger.component'
import TimePointsModal from './TriggerTemplates/TimePointsModal'
import ActionTriggerModal from './TriggerTemplates/ActionTriggerModal'
import { MessagePreviewModal } from '../..'

interface TriggersProps {
  campaignData: ICampaignRequest
  handleChange: (
    field: string,
    value: ICampaignTriggerMessage[] | ICampaignTriggerKeyword[],
  ) => void
  previewMessage: string | null
  showTimePointModal: boolean
  showActionTriggerModal: boolean
  setShowTimePointModal: Dispatch<SetStateAction<boolean>>
  setShowActionTriggerModal: Dispatch<SetStateAction<boolean>>
  setPreviewMessage: Dispatch<SetStateAction<string | null>>
}

const Triggers: React.FC<TriggersProps> = ({
  campaignData,
  handleChange,
  previewMessage,
  showTimePointModal,
  showActionTriggerModal,
  setShowTimePointModal,
  setShowActionTriggerModal,
  setPreviewMessage,
}) => {
  const [timePoints, setTimePoints] = useState<ICampaignTriggerMessage[]>(
    campaignData.campaign_trigger_message,
  )
  const [actionTrigger, setActionTrigger] = useState<ICampaignTriggerKeyword[]>(
    campaignData.campaign_trigger_keyword,
  )
  const [timePointsViewOnly, setTimePointsViewOnly] = useState(false)
  const [actionTriggerViewOnly, setActionTriggerViewOnly] = useState(false)
  const [selectedTimePoint, setSelectedTimePoint] =
    useState<ICampaignTriggerMessage | null>(null)
  const [selectedActionTrigger, setSelectedActionTrigger] =
    useState<ICampaignTriggerKeyword | null>(null)
  const [previewSubject, setPreviewSubject] = useState<string | undefined>()

  const handleSaveTimePoints = (
    data: ICampaignTriggerMessage,
    draft: boolean = false,
  ): void => {
    const dataToSave = {
      ...data,
      channel: campaignData.channel ?? 'SMS',
      status: draft ? 'Draft' : 'Active',
    }
    const newTimePoints = [...timePoints]
    const index = newTimePoints.findIndex(
      (trigger) =>
        trigger.campaign_trigger_message_id == data.campaign_trigger_message_id,
    )
    if (index !== -1) {
      newTimePoints[index] = dataToSave
    } else {
      newTimePoints.push(dataToSave)
    }
    setTimePoints(newTimePoints)
    handleChange('campaign_trigger_message', newTimePoints)
  }

  const handleSaveActionTrigger = (
    data: ICampaignTriggerKeyword,
    draft: boolean = false,
  ): void => {
    const dataToSave = { ...data, status: draft ? 'Draft' : 'Active' }
    const newActionTriggers = [...actionTrigger]
    const index = newActionTriggers.findIndex((trigger) =>
      compareCampaignTriggerKeywordIds(
        trigger.campaign_trigger_keyword_id,
        data.campaign_trigger_keyword_id,
      ),
    )
    if (index !== -1) {
      newActionTriggers[index] = dataToSave
    } else {
      newActionTriggers.push(dataToSave)
    }
    setActionTrigger(newActionTriggers)
    handleChange('campaign_trigger_keyword', newActionTriggers)
  }

  useEffect(() => {
    handleChange('campaign_trigger_message', timePoints)
  }, [timePoints])

  useEffect(() => {
    handleChange('campaign_trigger_keyword', actionTrigger)
  }, [actionTrigger])

  // Keep trigger channels in sync with campaign Send via from step 1.
  useEffect(() => {
    const channel = campaignData.channel ?? 'SMS'
    setTimePoints((prev) => {
      const next = prev.map((tp) =>
        tp.channel === channel ? tp : { ...tp, channel },
      )
      const changed = next.some((tp, i) => tp.channel !== prev[i]?.channel)
      return changed ? next : prev
    })
  }, [campaignData.channel])

  return (
    <>
      <div className="ac-triggers">
        <AccordionComponent
          id="time-points-accordion-id"
          key="time-points-accordion-key"
        >
          <AccordionItemsDirective>
            <AccordionItemDirective
              key="time-points-key"
              expanded={true}
              header={() => (
                <>
                  <span className="ac-input-label">Time points</span>
                  <Button
                    className="button-link"
                    onClick={() => {
                      setTimePointsViewOnly(false)
                      setSelectedTimePoint(null)
                      setShowTimePointModal(true)
                    }}
                    disabled={timePoints.length >= 5}
                  >
                    Add
                  </Button>
                </>
              )}
              content={() => (
                <div id="time-points-content">
                  <TimePointsTable
                    data={timePoints}
                    disableDuplicate={timePoints.length >= 5}
                    setData={setTimePoints}
                    onView={(data) => {
                      setTimePointsViewOnly(true)
                      setSelectedTimePoint(data)
                      setShowTimePointModal(true)
                    }}
                    handleEdit={(data) => {
                      setTimePointsViewOnly(false)
                      setSelectedTimePoint(data)
                      setShowTimePointModal(true)
                    }}
                  />
                </div>
              )}
            />
            <AccordionItemDirective
              key="action-trigger-key"
              expanded={true}
              header={() => (
                <>
                  <span className="ac-input-label">Action trigger</span>
                  <Button
                    className="button-link"
                    onClick={() => {
                      setActionTriggerViewOnly(false)
                      setSelectedActionTrigger(null)
                      setShowActionTriggerModal(true)
                    }}
                    disabled={actionTrigger.length >= 5}
                  >
                    Add
                  </Button>
                </>
              )}
              content={() => (
                <div id="action-trigger-content">
                  <ActionTrigger
                    data={actionTrigger}
                    disableDuplicate={actionTrigger.length >= 5}
                    setData={setActionTrigger}
                    handleView={(data) => {
                      setActionTriggerViewOnly(true)
                      setSelectedActionTrigger(data)
                      setShowActionTriggerModal(true)
                    }}
                    handleEdit={(data) => {
                      setActionTriggerViewOnly(false)
                      setSelectedActionTrigger(data)
                      setShowActionTriggerModal(true)
                    }}
                  />
                </div>
              )}
            />
          </AccordionItemsDirective>
        </AccordionComponent>
      </div>

      {previewMessage && (
        <MessagePreviewModal
          content={previewMessage}
          show={!!previewMessage}
          onHide={() => {
            setPreviewMessage(null)
            setPreviewSubject(undefined)
          }}
          channel={campaignData.channel}
          subject={previewSubject}
        />
      )}
      {showTimePointModal && (
        <TimePointsModal
          viewOnly={timePointsViewOnly}
          show={showTimePointModal}
          onHide={() => setShowTimePointModal(false)}
          initialData={selectedTimePoint ?? undefined}
          handleSaveDraft={(data) => handleSaveTimePoints(data, true)}
          handleSave={(data) => handleSaveTimePoints(data, false)}
          setPreviewMessage={setPreviewMessage}
          setPreviewSubject={setPreviewSubject}
          campaignSendChannel={campaignData.channel ?? 'SMS'}
        />
      )}
      {showActionTriggerModal && (
        <ActionTriggerModal
          viewOnly={actionTriggerViewOnly}
          show={showActionTriggerModal}
          onHide={() => setShowActionTriggerModal(false)}
          initialData={selectedActionTrigger ?? undefined}
          handleSaveDraft={(data) => handleSaveActionTrigger(data, true)}
          handleSave={(data) => handleSaveActionTrigger(data, false)}
          setPreviewMessage={setPreviewMessage}
          setPreviewSubject={setPreviewSubject}
          sendViaChannel={campaignData.channel ?? 'SMS'}
        />
      )}
    </>
  )
}

export default Triggers
